import type {
    Callback1,
    LogoMediaObjectParams,
    MediaBatchContext,
    UploadResponse,
    FileMediaType as FileMediaTypeType
} from '@wix/document-services-types'
import {FileMediaType} from '../../../constants/dsTypesConstants'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import {fetchLogoMediaObject, LogoRequestParams} from './requests'
import type {LoggerExtAPI} from '../../logger'
import {
    type EventHandlerExtApi,
    type Handler,
    HandlerStatus,
    type PayloadData,
    PayloadStatus
} from '../eventHandlerExtension'
import {UploadConfig, uploadFilesWithHandlersSync, uploadSingleFileWithHandler} from '../fileUploadHandler'
import {ERRORS, LOGS} from '../constants'
import {ReportableError} from '@wix/document-manager-utils'
import {handleAiError} from '../aiBuilderUtils'

const generateLogoTimeout = 120000
const caller = 'logo generation'

const createLogoGenerationHandler = (
    serverFacade: any,
    createExtArgs: CreateExtArgs,
    onError?: Callback1<any>,
    onSuccess?: Callback1<UploadResponse>
): Handler => {
    return async (data: PayloadData) => {
        const {logger} = createExtArgs.extensionAPI as LoggerExtAPI
        const {status, output} = data ?? {}
        if (status === PayloadStatus.COMPLETED) {
            logger.interactionEnded(LOGS.IMAGES.GENERATION, {tags: {caller, extras: {output}}})
            const logoUrl = output.image_url

            const onSuccessWithLog = (arg: any) => {
                logger.interactionEnded(caller, {tags: {caller}})
                onSuccess?.(arg)
            }
            const onErrorWithLog = (error: any) => {
                const reportableError = new ReportableError({
                    errorType: ERRORS.IMAGES.UPLOAD,
                    message: 'Images upload failed',
                    extras: {
                        originalError: error
                    }
                })
                logger.captureError(reportableError, {tags: {caller}})
                onError?.(error)
            }
            const logoContext: MediaBatchContext<any> = {
                createExtArgs,
                idMap: {},
                originalData: {},
                onSuccess: onSuccessWithLog,
                onError: onErrorWithLog,
                failures: [],
                totalItems: 1,
                processedItems: 0,
                serverFacade,
                mediaType: FileMediaType.IMAGE as FileMediaTypeType,
                caller
            }
            const logoUploadConfig: UploadConfig<UploadResponse> = {
                extractData: (uploadOutput: UploadResponse) => uploadOutput,
                transformData: (imageData: UploadResponse) => imageData,
                injectItem: () => {},
                errorMessage: 'Failed to upload logo',
                timeOutInMS: 30000,
                onItemUploadStart: (url: string) => {
                    logger.interactionStarted(LOGS.IMAGES.UPLOAD, {tags: {caller}, extras: {url}})
                },
                onItemUploadComplete: (url: string) => {
                    logger.interactionEnded(LOGS.IMAGES.UPLOAD, {tags: {caller}, extras: {url}})
                }
            }
            const {coreConfig} = createExtArgs
            const {experimentInstance} = coreConfig
            if (experimentInstance.isOpen('dm_uploadSync')) {
                await uploadFilesWithHandlersSync(logoContext, logoUploadConfig, {logo: logoUrl})
            } else {
                await uploadSingleFileWithHandler(logoContext, logoUploadConfig, 'logo', logoUrl)
            }
            return {status: HandlerStatus.COMPLETED}
        }
        onError?.({errors: ['Logo generation failed']})
        return {status: HandlerStatus.FAILED}
    }
}

export const getLogoMediaObject = async (
    {serverFacade}: Pick<EnvironmentContext, 'serverFacade'>,
    createExtArgs: CreateExtArgs,
    {
        businessName,
        headerBg,
        font,
        industryTerm,
        colorPalette,
        siteDescription,
        userRequest,
        userRequestInfo,
        onSuccess,
        onError
    }: LogoMediaObjectParams
): Promise<void> => {
    const requestBody: LogoRequestParams = {
        Business_Name: businessName,
        Font: font,
        Industry_Term: industryTerm,
        Site_Description: siteDescription,
        color_palette: colorPalette,
        header_bg: headerBg,
        user_Request: userRequest,
        userRequestInfo
    }

    const {extensionAPI} = createExtArgs
    const {eventHandler, logger} = extensionAPI as LoggerExtAPI & EventHandlerExtApi
    logger.interactionStarted(caller, {tags: {caller}})

    try {
        logger.interactionStarted(LOGS.IMAGES.GENERATION, {tags: {caller}})
        const generationExecutionId = await fetchLogoMediaObject(serverFacade, requestBody)
        eventHandler.registerExecutionIdToHandler(
            generationExecutionId,
            createLogoGenerationHandler(serverFacade, createExtArgs, onError, onSuccess),
            caller,
            generateLogoTimeout
        )
    } catch (error) {
        handleAiError(error, ERRORS.IMAGES.GENERATION, logger, onError, {caller})
    }
}
